import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
const recommendationCache = new Map();

class CartDrawerComponent extends Component {
  #recommendationRequest;
  #variantCache = new WeakMap();
  #recommendationObserver;
  #recommendationFrame;

  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#handleUpsellClick);
    this.#recommendationObserver = new MutationObserver(() => {
      if (this.#recommendationFrame) return;
      this.#recommendationFrame = requestAnimationFrame(() => {
        this.#recommendationFrame = undefined;
        this.#loadRecommendations();
      });
    });
    this.#recommendationObserver.observe(this, { childList: true, subtree: true });
    this.addEventListener('scroll', this.#handleDrawerScroll, true);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    this.addEventListener('click', this.#handleInstallmentsClick, true);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#recommendationRequest?.abort();
    this.#recommendationObserver?.disconnect();
    cancelAnimationFrame(this.#recommendationFrame);
    this.removeEventListener('click', this.#handleUpsellClick);
    this.removeEventListener('scroll', this.#handleDrawerScroll, true);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    this.removeEventListener('click', this.#handleInstallmentsClick, true);
  }

  /**
   * Recomputes the sticky summary after a back/forward cache restore re-renders the cart items.
   *
   * On viewports at or above 990px an open drawer survives a navigation and is reopened during
   * parsing (see the restore script in `snippets/theme-drawer.liquid`), so no open event fires
   * on the way back and the measurement taken before the shopper left is the one still applied.
   * An empty drawer measured as unpinned would keep the checkout CTA scrolling away once the
   * restore fills it with line items.
   */
  #handleCartSectionRestored = () => {
    requestAnimationFrame(() => { this.#updateStickyState(); this.#refreshUpsells(); this.#updateScrollFades(); });
  };

  /** Handles the theme-drawer opening by updating its sticky state. */
  #handleDrawerOpen = () => {
    this.#refreshUpsells();
    this.#updateScrollFades();
    this.#updateStickyState();
  };

  /**
   * Closes the cart drawer before the installments dialog opens.
   *
   * The payment-terms element replaces its shadow content when cart totals change,
   * so listen on this stable ancestor instead of binding to a particular CTA instance.
   *
   * @param {MouseEvent} event
   */
  #handleInstallmentsClick = (event) => {
    const path = event.composedPath();
    const ctaIndex = path.findIndex(
      (element) => element instanceof Element && element.id === 'shopify-installments-cta'
    );
    if (ctaIndex === -1) return;

    const ctaRoot = path.slice(ctaIndex + 1).find((element) => element instanceof ShadowRoot);
    if (!ctaRoot?.host.matches('shopify-payment-terms')) return;

    this.#themeDrawer?.close();
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const source = event.target instanceof Element ? event.target : null;
    const collectionQuickAdd = Boolean(document.querySelector('rc-collection-promo-bar')) && Boolean(source?.closest('product-card, quick-add-dialog, quick-add-component, .rc-qs-sheet'));
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !collectionQuickAdd && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(async ({ detail }) => {
        await this.querySelector('cart-items-component')?.cartRenderComplete;
        this.#refreshUpsells();
        const settle = () => requestAnimationFrame(() => { this.#updateStickyState(); this.#refreshUpsells(); this.#updateScrollFades(); });

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  async #loadRecommendations() {
    if (!this.#themeDrawer?.isOpen) return;
    const target = this.querySelector('[data-ruido-recommendations]');
    if (!target || target.dataset.loaded) return;
    const key = `${target.dataset.url}:${target.dataset.cartKey}`;
    target.dataset.loaded = 'loading';
    this.#recommendationRequest?.abort();
    const controller = new AbortController();
    this.#recommendationRequest = controller;
    try {
      let html = recommendationCache.get(key);
      if (!html) {
        const response = await fetch(target.dataset.url, { signal: controller.signal });
        if (!response.ok) throw new Error('Recommendations unavailable');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const recommendations = doc.querySelector('.ruido-cart-upsells');
        if (!recommendations) throw new Error('Recommendations unavailable');
        html = recommendations.outerHTML;
        if (recommendationCache.size >= 8) recommendationCache.delete(recommendationCache.keys().next().value);
        recommendationCache.set(key, html);
      }
      if (!target.isConnected || controller.signal.aborted) return;
      target.innerHTML = html;
      target.dataset.loaded = 'true';
      this.#refreshUpsells();
      this.#updateScrollFades();
    } catch (error) {
      if (error.name !== 'AbortError') console.warn('[RUIDO recommendations]', error);
      delete target.dataset.loaded;
    }
  }

  #refreshUpsells = () => {
    this.#loadRecommendations();
    for (const card of this.querySelectorAll('[data-ruido-upsell-card]')) {
      this.#refreshUpsellSizes(card);
      this.#syncUpsellVariant(card);
    }
  };

  #handleUpsellClick = (event) => {
    if (!(event.target instanceof Element)) {
      return;
    }

    const option = event.target.closest(
      '[data-upsell-color], [data-upsell-size], [data-upsell-add]'
    );

    if (
      !(option instanceof HTMLButtonElement) ||
      option.disabled
    ) {
      return;
    }

    const card =
      /** @type {HTMLElement | null} */ (
        option.closest('[data-ruido-upsell-card]')
      );

    if (!card) {
      return;
    }

    /*
     * Selección de color.
     */
    if (option.hasAttribute('data-upsell-color')) {
      card
        .querySelectorAll('[data-upsell-color]')
        .forEach((button) => {
          const selected = button === option;

          button.classList.toggle(
            'is-selected',
            selected
          );

          button.setAttribute(
            'aria-pressed',
            selected ? 'true' : 'false'
          );
        });

      this.#refreshUpsellSizes(card);
      this.#syncUpsellVariant(card);
      return;
    }

    const variants = this.#getUpsellVariants(card);

    const colorIndex = this.#getOptionIndex(
      card.dataset.colorIndex
    );

    const sizeIndex = this.#getOptionIndex(
      card.dataset.sizeIndex
    );

    const selectedColor = card
      .querySelector(
        '[data-upsell-color].is-selected'
      )
      ?.getAttribute('data-upsell-color');

    const selectedSize = option.getAttribute(
      'data-upsell-size'
    );

    /*
     * Encuentra la variante exacta del color
     * y talla seleccionados.
     */
    const variant = variants.find((item) => {
      if (!item.available) {
        return false;
      }

      if (
        colorIndex >= 0 &&
        selectedColor &&
        !this.#optionMatches(
          item.options[colorIndex],
          selectedColor
        )
      ) {
        return false;
      }

      if (
        sizeIndex >= 0 &&
        selectedSize &&
        !this.#optionMatches(
          item.options[sizeIndex],
          selectedSize
        )
      ) {
        return false;
      }

      return true;
    });

    if (!variant) {
      return;
    }

    const form =
      /** @type {HTMLFormElement | null} */ (
        card.querySelector('form')
      );

    const variantInput =
      /** @type {HTMLInputElement | null} */ (
        form?.querySelector('input[name="id"]')
      );

    if (!form || !variantInput) {
      return;
    }

    /*
     * Garantiza que se agregue la variante elegida.
     */
    variantInput.value = String(variant.id);

    variantInput.dispatchEvent(
      new Event('change', {
        bubbles: true
      })
    );

    option.classList.add('is-adding');

    form.requestSubmit();

    window.setTimeout(() => {
      option.classList.remove('is-adding');
    }, 900);
  };

  /**
   * Activa o desactiva tallas según
   * el color seleccionado.
   *
   * @param {HTMLElement} card
   */
  #refreshUpsellSizes = (card) => {
    const variants = this.#getUpsellVariants(card);

    const colorIndex = this.#getOptionIndex(
      card.dataset.colorIndex
    );

    const sizeIndex = this.#getOptionIndex(
      card.dataset.sizeIndex
    );

    const selectedColor = card
      .querySelector(
        '[data-upsell-color].is-selected'
      )
      ?.getAttribute('data-upsell-color');

    card
      .querySelectorAll('[data-upsell-size]')
      .forEach((element) => {
        if (
          !(element instanceof HTMLButtonElement)
        ) {
          return;
        }

        const size = element.getAttribute(
          'data-upsell-size'
        );

        element.disabled = !variants.some(
          (variant) => {
            if (!variant.available) {
              return false;
            }

            if (
              colorIndex >= 0 &&
              selectedColor &&
              !this.#optionMatches(
                variant.options[colorIndex],
                selectedColor
              )
            ) {
              return false;
            }

            return (
              sizeIndex < 0 ||
              this.#optionMatches(
                variant.options[sizeIndex],
                size
              )
            );
          }
        );
      });

    /*
     * Productos que no requieren talla.
     */
    const directAdd = card.querySelector(
      '[data-upsell-add]'
    );

    if (
      directAdd instanceof HTMLButtonElement
    ) {
      directAdd.disabled = !variants.some(
        (variant) => {
          if (!variant.available) {
            return false;
          }

          return (
            colorIndex < 0 ||
            !selectedColor ||
            this.#optionMatches(
              variant.options[colorIndex],
              selectedColor
            )
          );
        }
      );
    }
  };

  /**
   * Sincroniza la variante, imagen y precio
   * al seleccionar un color.
   *
   * @param {HTMLElement} card
   */
  #syncUpsellVariant = (card) => {
    const variants = this.#getUpsellVariants(card);

    const colorIndex = this.#getOptionIndex(
      card.dataset.colorIndex
    );

    const selectedColor = card
      .querySelector(
        '[data-upsell-color].is-selected'
      )
      ?.getAttribute('data-upsell-color');

    const variant = variants.find((item) => {
      if (!item.available) {
        return false;
      }

      if (
        colorIndex < 0 ||
        !selectedColor
      ) {
        return true;
      }

      return this.#optionMatches(
        item.options[colorIndex],
        selectedColor
      );
    });

    if (!variant) {
      return;
    }

    /*
     * Actualiza el ID interno de la variante.
     */
    const variantInput = card.querySelector(
      'form input[name="id"]'
    );

    if (
      variantInput instanceof HTMLInputElement
    ) {
      variantInput.value = String(variant.id);

      variantInput.dispatchEvent(
        new Event('change', {
          bubbles: true
        })
      );
    }

    /*
     * Usa la URL exacta enviada por Liquid.
     */
    const imageUrl =
      variant.image ||
      variant.featured_image?.src ||
      variant.featured_media?.preview_image?.src;

    const image = card.querySelector(
      '.ruido-cart-upsell__image img'
    );

    if (
      imageUrl &&
      image instanceof HTMLImageElement
    ) {
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');

      image.src = imageUrl;
    }

    /*
     * Actualiza el precio cuando cambia
     * entre variantes.
     */
    const price = card.querySelector(
      '.ruido-cart-upsell__price'
    );

    if (
      price &&
      Number.isFinite(Number(variant.price))
    ) {
      const currency =
        window.Shopify?.currency?.active ||
        'MXN';

      const formatted = new Intl.NumberFormat(
        'es-MX',
        {
          style: 'currency',
          currency
        }
      ).format(Number(variant.price) / 100);

      price.textContent = /[A-Z]{3}/.test(
        price.textContent || ''
      )
        ? `${formatted} ${currency}`
        : formatted;
    }
  };

  /**
   * Compara opciones ignorando espacios y
   * diferencias entre mayúsculas y minúsculas.
   *
   * @param {unknown} first
   * @param {unknown} second
   */
  #optionMatches(first, second) {
    return (
      String(first ?? '')
        .trim()
        .toLocaleLowerCase('es-MX') ===
      String(second ?? '')
        .trim()
        .toLocaleLowerCase('es-MX')
    );
  }

  /**
   * Obtiene las variantes guardadas en la tarjeta.
   *
   * @param {HTMLElement} card
   */
  #getUpsellVariants(card) {
    if (this.#variantCache.has(card)) return this.#variantCache.get(card);
    const script = card.querySelector('[data-upsell-variants]');

    if (!script?.textContent) {
      return [];
    }

    try {
      const variants = JSON.parse(script.textContent);
      this.#variantCache.set(card, variants);
      return variants;
    } catch {
      return [];
    }
  }

  /**
   * Convierte el índice de la opción a número.
   *
   * @param {string | undefined} value
   */
  #getOptionIndex(value) {
    if (
      value === undefined ||
      value === ''
    ) {
      return -1;
    }

    const index = Number(value);

    return Number.isInteger(index)
      ? index
      : -1;
  }

  /**
   * Comprueba si el carrito está vacío.
   */

  /**
   * Detecta el desplazamiento dentro
   * de productos y upsells.
   *
   * @param {Event} event
   */
  #handleDrawerScroll = (event) => {
    if (
      event.target instanceof Element &&
      event.target.matches(
        '.ruido-cart-scroll-region'
      )
    ) {
      this.#updateScrollFades(event.target);
    }
  };

  /**
   * Activa el desvanecido únicamente dentro
   * de la lista de productos y upsells.
   *
   * @param {Element | null} [content]
   */
  #updateScrollFades = (content = null) => {
    const scrollArea =
      content ||
      this.#dialog?.querySelector(
        '.ruido-cart-scroll-region'
      );

    if (
      !(scrollArea instanceof HTMLElement)
    ) {
      return;
    }

    const maxScroll = Math.max(
      0,
      scrollArea.scrollHeight -
        scrollArea.clientHeight
    );

    scrollArea.classList.toggle(
      'ruido-can-scroll-up',
      scrollArea.scrollTop > 2
    );

    scrollArea.classList.toggle(
      'ruido-can-scroll-down',
      scrollArea.scrollTop < maxScroll - 2
    );
  };


  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
